import React from 'react';
import { Badge } from "@/components/ui/badge";
import { MapPin, User, Phone } from "lucide-react";
import { STATUS_STYLES, typeLabel, treatmentLabel, assetUrl } from './incidentConstants';

const Field = ({ label, value }) => (value ? (
  <div><p className="text-xs text-stone-500">{label}</p><p className="text-sm text-stone-900 whitespace-pre-wrap">{value}</p></div>
) : null);

export const IncidentDetail = ({ report }) => {
  const status = STATUS_STYLES[report.status] || STATUS_STYLES.submitted;
  return (
    <div className="space-y-6" data-testid="incident-detail">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{typeLabel(report.incident_type)}</Badge>
        <Badge className={status.className} data-testid="incident-detail-status">{status.label}</Badge>
        {report.injury_occurred && report.incident_type !== 'injury' && <Badge className="bg-rose-100 text-rose-700">Injury</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Date & time" value={`${report.incident_date} at ${report.incident_time}`} />
        <Field label="Reported by" value={report.reported_by_name} />
        <Field label="Site" value={report.project_name || 'No site'} />
        <Field label="Place" value={report.location} />
      </div>
      {report.latitude != null && (
        <a href={`https://maps.google.com/?q=${report.latitude},${report.longitude}`} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 flex items-center gap-1 hover:underline" data-testid="incident-map-link">
          <MapPin className="w-4 h-4" /> View GPS location on map
        </a>
      )}
      <Field label="What happened" value={report.description} />
      <Field label="Actions taken right away" value={report.immediate_actions} />
      {report.injury_occurred && (
        <div className="p-4 bg-rose-50 rounded-xl grid grid-cols-2 gap-4">
          <Field label="Injured person" value={`${report.injured_person_name}${report.injured_person_role ? ` (${report.injured_person_role})` : ''}`} />
          <Field label="Treatment" value={report.treatment && treatmentLabel(report.treatment)} />
          <Field label="Body parts" value={report.body_parts} />
          <Field label="Injury" value={report.injury_description} />
        </div>
      )}
      {report.witnesses?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-stone-500">Witnesses</p>
          {report.witnesses.map((w, i) => (
            <p key={i} className="text-sm flex items-center gap-3"><User className="w-4 h-4 text-stone-400" />{w.name}{w.phone && <span className="flex items-center gap-1 text-stone-500"><Phone className="w-3 h-3" />{w.phone}</span>}</p>
          ))}
        </div>
      )}
      {report.photo_urls?.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {report.photo_urls.map((url, i) => (
            <a key={url} href={assetUrl(url)} target="_blank" rel="noreferrer" data-testid={`incident-detail-photo-${i}`}>
              <img src={assetUrl(url)} alt={`Incident photo ${i + 1}`} className="w-full aspect-square object-cover rounded-lg" />
            </a>
          ))}
        </div>
      )}
      <div className="pt-4 border-t border-stone-200">
        <p className="text-xs text-stone-500 mb-1">Signed by {report.signed_name}</p>
        {report.signature_url && <img src={assetUrl(report.signature_url)} alt="Signature" className="h-20 bg-white border rounded-lg" data-testid="incident-detail-signature" />}
      </div>
      {report.admin_notes && <Field label={`Admin notes${report.reviewed_by_name ? ` — ${report.reviewed_by_name}` : ''}`} value={report.admin_notes} />}
    </div>
  );
};
